import { useEffect, useState } from 'react';
import type { IAuthorization } from '@frontend/components/interfaces/authorization';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IServiceSupervisor } from '@frontend/components/interfaces/serviceSupervisor';
import { ManagerAuthorization } from '@common/enums';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    Link,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { FormatCurrency } from '@frontend/components/elements/FormatCurrency';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { FullWidthTemplate } from '@frontend/components/templates/FullWidthTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import type { PractitionerDetailsTabProps } from './PractitionerDetails';

interface IAuthedServiceSupervisor extends IServiceSupervisor {
    authorization: IAuthorization;
}

export function PractitionerServiceSupervisorsList(props: Readonly<PractitionerDetailsTabProps>) {
    const { practitionerId } = props;
    const [serviceSupervisors, setServiceSupervisors] = useState<IAuthedServiceSupervisor[]>([]);
    const [employments, setEmployments] = useState<IEmployment[]>([]);

    useEffect(() => {
        loadServiceSupervisors(practitionerId);
        loadEmployments(practitionerId);
    }, [practitionerId]);

    return (
        <FullWidthTemplate>
            <Header
                actionButtons={
                    employments.length > 0
                        ? [
                              <LinkButton
                                  icon="icon icon-add"
                                  key="add-button"
                                  to={`/serviceSupervisors/add?practitionerId=${practitionerId}&returnTo=practitioner`}
                              >
                                  {translateKey('common.addItem', translateKey('supervisors.supervisor'))}
                              </LinkButton>,
                          ]
                        : []
                }
                content={translateKey('supervisors.supervisor')}
            />

            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('common.name')}</TableHeader>
                        <TableHeader>{translateKey('service.providingForOrganization')}</TableHeader>
                        <TableHeader>{translateKey('supervisors.payout')}</TableHeader>
                        <TableHeader>{translateKey('common.startDate')}</TableHeader>
                        <TableHeader>{translateKey('common.endDate')}</TableHeader>
                        <TableHeader align="right"></TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {serviceSupervisors.map((item) => {
                        return (
                            <TableRow key={item.id}>
                                <TableData>{item.service.name}</TableData>
                                <TableData>
                                    {hasOneAuthorization(ManagerAuthorization.serviceRightsOrganizations) ? (
                                        <Link
                                            to={`/organizations/${item.employment.organization.id}/?tab=serviceOrganizationSettings`}
                                        >
                                            {item.employment.organization.name}
                                        </Link>
                                    ) : (
                                        item.employment.organization.name
                                    )}
                                </TableData>
                                <TableData>
                                    <FormatCurrency value={item.compensationPayOut} />
                                </TableData>
                                <TableData>
                                    <NullableFormattedDate value={item.startDate} />
                                </TableData>
                                <TableData>
                                    <NullableFormattedDate value={item.endDate} />
                                </TableData>

                                <TableData align="right">
                                    {item.authorization.canUpdate && (
                                        <EditButton
                                            to={`/serviceSupervisors/${item.id}?practitionerId=${practitionerId}&returnTo=practitioner`}
                                        />
                                    )}
                                    {item.authorization.canUpdate && item.endDate === null && (
                                        <DeleteButton onClick={async () => await remove(item.id)} />
                                    )}
                                </TableData>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </AdminTable>
        </FullWidthTemplate>
    );

    async function loadServiceSupervisors(id: string) {
        const autorizationQuery = 'includeItemAuthorizations=canUpdate&includeListAuthorizations=canAdd';
        const url = `/api/serviceSupervisors?practitionerId=${id}&${autorizationQuery}`;
        const result = await fetchJson(url, 'GET');
        if (result.json.items) {
            setServiceSupervisors(result.json.items);
        }
    }

    async function loadEmployments(id: string) {
        const url = `/api/employments/${id}/organizations?isActive=true&isNoManager=true`;
        const results = await fetchJson(url, 'GET');
        if (results.json.items) {
            setEmployments(results.json.items);
        }
    }

    async function remove(id: string) {
        const url = `/api/serviceSupervisors/${id}`;
        const result = await fetchJson(url, 'POST', { endDateIsCurrentDate: true });
        if (result.json) {
            await loadServiceSupervisors(practitionerId);
        }
    }
}
